import {
  DEFAULT_LOCALE_CODE,
  LOCALE_CHANGE_EVENT,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALE_CODES
} from '../core/constants';
import { SupportedLocaleCode } from '../core/types';

/**
 * Adds an event listener for localeChange custom events. It returns an unsubscriber function.
 * @param handler Function to be called when a localeChange event is detected
 */
export const addLocaleChangeEventListener = (handler: (newLocale: SupportedLocaleCode) => void) => {
  const fn = (event: Event) => {
    const customEvent = event as CustomEvent;
    const eventLocale = customEvent?.detail?.locale;
    if (eventLocale && SUPPORTED_LOCALE_CODES.includes(eventLocale)) {
      handler(eventLocale);
    }
  };
  window.addEventListener(LOCALE_CHANGE_EVENT, fn);

  return () => {
    window.removeEventListener(LOCALE_CHANGE_EVENT, fn);
  };
};

/**
 * Dispatches a locale change custom event  which will cause the app language to update.
 * The event will not fire if an invalid / unsupported locale code is supplied.
 */
export const dispatchLocaleChangeEvent = (newLocale: string) => {
  if (SUPPORTED_LOCALE_CODES.includes(newLocale as SupportedLocaleCode)) {
    const LOCALE_CHANGE_EVENTEvent = new CustomEvent(LOCALE_CHANGE_EVENT, {
      detail: { locale: newLocale }
    });
    window.dispatchEvent(LOCALE_CHANGE_EVENTEvent);
  }
};

/**
 * Retrieves and validates the saved locale from local storage, otherwise returns the default locale
 */
export const getLocalStorageLocale = (): SupportedLocaleCode => {
  const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (!savedLocale) {
    return DEFAULT_LOCALE_CODE;
  }
  if (SUPPORTED_LOCALE_CODES.indexOf(savedLocale as SupportedLocaleCode) === -1) {
    return DEFAULT_LOCALE_CODE;
  }
  return savedLocale as SupportedLocaleCode;
};

/**
 * Validates a locale code against the list of supported locale codes
 * @param localeCode The locale code to validate
 */
export const isValidLocaleCode = (localeCode: string) => {
  return SUPPORTED_LOCALE_CODES.includes(localeCode as SupportedLocaleCode);
};
